<template>
	<div
		id="advanced-options-panel"
		:class="{
			'advanced-options': true,
			'p-0': true,
			expanded: isExpanded
		}"
	>
		<button
			type="button"
			id="advLoadShift"
			@click.prevent="isExpanded = !isExpanded"
			:class="{
				'btn': true,
				'btn-box': true,
				'js-adv-options': true,
				'align-left': true,
				'toggled': isExpanded
			}"
			:aria-expanded="isExpanded"
		>
			<span>Advanced Options</span>

			<UseIcon iconClass="symbol" asset="sprite.svg#icon-gears" />

			<i class="symbol secondary close xl"></i>
		</button>

		<div v-show="isExpanded" class="container no-gutters adv-option-groups p-xl-3">
			<div class="row no-gutters py-3 justify-content-around">
				<div class="col-1"></div>

				<div class="col-3 pe-3">
					<label class="text-center d-block border-bottom pb-2">Aquastat Setpoints</label>
				</div>

				<div class="col-5 ps-3">
					<label class="text-center d-block border-bottom pb-2">Temperature Setpoints</label>
				</div>
			</div>

			<div class="row no-gutters py-3 justify-content-around">
				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">EcoPort Command
					</label>
					<Tooltip
						:title="inputConfig.ecoport_commands.label"
						align="right"
					>
						<p>
							{{ inputConfig.ecoport_commands.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">ON Sensor</label>
					<Tooltip
						:title="inputConfig.aquastat_controls.label"
						align="right"
					>
						<p>
							{{ inputConfig.aquastat_controls.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">OFF Sensor</label>
					<Tooltip
						title="OFF Sensor"
						align="right"
					>
						<p>
							OFF Sensor percentage setting
						</p>
					</Tooltip>
				</div>

				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">ON-Trigger</label>
					<Tooltip
						:title="inputConfig.on_trigger.label"
						align="left"
					>
						<p>
							{{ inputConfig.on_trigger.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">OFF-Trigger</label>
					<Tooltip
						:title="inputConfig.off_trigger.label"
						align="left"
					>
						<p>
							{{ inputConfig.off_trigger.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">HPWH Outlet</label>
					<Tooltip
						:title="inputConfig.hpwh_outlet.label"
						align="left"
					>
						<p>
							{{ inputConfig.hpwh_outlet.definition }}
						</p>
					</Tooltip>
				</div>
			</div>

			<div class="row no-gutters justify-content-around pb-4">
				<div class="col-2 flex items-center gap-2">
					<label class="text-center btn-help">Normal</label>
					<Tooltip
						:title="inputConfig.normal_controls.label"
						align="right"
					>
						<p>
							{{ inputConfig.normal_controls.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.aquastatFraction"
								min="5"
								max="55"
									>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.offSensor_normal"
								min="5"
								max="55"
									>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsNorm_TempSetOnTrig"
								min="32"
								max="212"
									>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsNorm_TempSetOffTrig"
								min="32"
								max="212"
									>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.storageT"
								min="32"
								max="212"
									>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
			</div>

			<div :class="['row', 'no-gutters', 'justify-content-around', 'pb-4', { 'disabled-row': !isAnyPeriodEnabled }]">
				<div class="col-2 p-2 flex items-center gap-2">
					<label class="text-center btn-help">Load Up</label>
					<Tooltip
						:title="inputConfig.loadUp_controls.label"
						align="right"
					>
						<p>
							{{ inputConfig.loadUp_controls.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsLoadUp_AquaFrac"
								min="5"
								max="45"
								:disabled="!isAnyPeriodEnabled"
							>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsLoadUp_OffSensor"
								min="0"
								max="45"
								:disabled="!isAnyPeriodEnabled"
							>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsLoadUp_TempSetOnTrig"
								min="32"
								max="212"
								:disabled="!isAnyPeriodEnabled"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsLoadUp_TempSetOffTrig"
								min="32"
								max="212"
								:disabled="!isAnyPeriodEnabled"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.lsLoadUp_TempSetHPWHOut"
								min="32"
								max="212"
								:disabled="!isAnyPeriodEnabled"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
			</div>

			<div :class="['row', 'no-gutters', 'justify-content-around', 'pb-4', { 'disabled-row': !isAnyPeriodEnabled || isDualFuelSchematic }]">
				<div class="col-2 p-2 flex items-center gap-2">
					<label class="text-center btn-help">Shed</label>
					<Tooltip
						:title="inputConfig.shed_controls.label"
						align="right"
					>
						<p>
							{{ inputConfig.shed_controls.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								:type="isDualFuelSchematic ? 'text' : 'number'"
								:value="isDualFuelSchematic ? 'OFF' : formData.lsShed_AquaFrac"
								@input="formData.lsShed_AquaFrac = $event.target.value"
								min="5"
								max="85"
								:disabled="!isAnyPeriodEnabled || isDualFuelSchematic"
							>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								:type="isDualFuelSchematic ? 'text' : 'number'"
								:value="isDualFuelSchematic ? 'OFF' : formData.lsShed_OffSensor"
								@input="formData.lsShed_OffSensor = $event.target.value"
								min="5"
								max="85"
								:disabled="!isAnyPeriodEnabled || isDualFuelSchematic"
							>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								:type="isDualFuelSchematic ? 'text' : 'number'"
								:value="isDualFuelSchematic ? 'OFF' : formData.lsShed_TempSetOnTrig"
								@input="formData.lsShed_TempSetOnTrig = $event.target.value"
								min="32"
								max="212"
								:disabled="!isAnyPeriodEnabled || isDualFuelSchematic"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								:type="isDualFuelSchematic ? 'text' : 'number'"
								:value="isDualFuelSchematic ? 'OFF' : formData.lsShed_TempSetOffTrig"
								@input="formData.lsShed_TempSetOffTrig = $event.target.value"
								min="32"
								max="212"
								:disabled="!isAnyPeriodEnabled || isDualFuelSchematic"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								:type="isDualFuelSchematic ? 'text' : 'number'"
								:value="isDualFuelSchematic ? 'OFF' : formData.lsShed_TempSetHPWHOut"
								@input="formData.lsShed_TempSetHPWHOut = $event.target.value"
								min="32"
								max="212"
								disabled
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
			</div>

			<div v-if="isGasParallel" class="row no-gutters justify-content-around pb-4">
				<div class="col-2 p-2 flex items-center gap-2">
					<label class="text-center btn-help">Gas Heater</label>
					<Tooltip
						:title="inputConfig.gasHeater_controls.label"
						align="right"
					>
						<p>
							{{ inputConfig.gasHeater_controls.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.gasHeater_AquaFrac"
								min="5"
								max="85"
							>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.gasHeater_OffSensor"
								min="5"
								max="85"
							>

							<UseIcon asset="sprite.svg#symbol-percent" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.gasHeater_TempSetOnTrig"
								min="32"
								max="212"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.gasHeater_TempSetOffTrig"
								min="32"
								max="212"
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.gasHeater_TempSetHPWHOut"
								min="32"
								max="212"
								disabled
							>

							<UseIcon asset="sprite.svg#symbol-fahrenheit" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>
			</div>

			<div :class="['row', 'no-gutters', 'justify-content-around', 'pb-4', { 'disabled-row': !isAnyPeriodEnabled }]">
				<div class="col-2 p-2 flex items-center gap-2">
					<label class="text-center btn-help">Load Up Hours</label>
					<Tooltip
						:title="inputConfig.loadup_hours.label"
						align="right"
						topOffset="-20px"
					>
						<p>
							{{ inputConfig.loadup_hours.definition }}
						</p>
					</Tooltip>
				</div>

				<div class="col-2 p-2">
					<div class="field-wrap">
						<div class="row no-gutters field-unit">
							<input
								type="number"
								v-model="formData.loadup_hours"
								min="1"
								max="6"
								:disabled="!isAnyPeriodEnabled"
							>

							<i class="icon fa-regular fa-clock flex align-items-center justify-content-center" />
						</div>

						<div class="suffix-wrap">
							<p class="desc"></p>
						</div>
					</div>
				</div>

				<div class="col-2 p-2"></div>
				<div class="col-2 p-2"></div>
				<div class="col-2 p-2"></div>
				<div class="col-2 p-2"></div>
			</div>
		</div>
	</div>
</template>

<script>
import UseIcon from '@/components/UseIcon'
import formData from '@/components/Sizer/form-data-store'
import Tooltip from '@/components/Tooltip'
import inputConfig from '@/components/FormInput/input-config'

export default {
	name: 'AdvancedLoadShiftOpts',

	components: {
		UseIcon,
		Tooltip
	},

	data: () => ({
		formData,
		userToggledAdvancedOpts: true,
		inputConfig,
		isExpanded: false
	}),

	computed: {
		expandAdvancedOpts () {
			// Always expanded
			return true
		},

		isAnyPeriodEnabled () {
			return this.formData.LSperiod1_bool || this.formData.LSperiod2_bool
		},

		isDualFuelSchematic () {
			return ['sprtp_series', 'mprtp_series', 'gas_swingtank', 'gas_parallel'].includes(this.formData.schematic)
		},

		isGasParallel () {
			return this.formData.schematic === 'gas_parallel'
		},

	},

	watch: {
		'formData.storageT'(newVal) {
			if (newVal !== null && newVal !== undefined) {
				this.formData.lsShed_TempSetHPWHOut = newVal;
				this.formData.gasHeater_TempSetHPWHOut = newVal;
				if (newVal > this.formData.lsLoadUp_TempSetHPWHOut || !this.formData.loadShiftToggled) {
					this.formData.lsLoadUp_TempSetHPWHOut = newVal;
				}
			}
		},

		'formData.schematic': {
			handler(newSchematic) {
				// Supplemental heating variants inherit the defaults of the
				// base schematic they are built on.
				if (['mprtp', 'mprtp_series'].includes(newSchematic)) {
					// Normal row - locked defaults for mprtp
					this.formData.storageT = 140
					this.formData.aquastatFraction = 20
					this.formData.lsNorm_TempSetOnTrig = 125
					this.formData.offSensor_normal = 20
					this.formData.lsNorm_TempSetOffTrig = 140

					// Lock supply water temperature for mprtp
					this.formData.supplyT = 125

					// Shed row
					this.formData.lsShed_AquaFrac = 20
					this.formData.lsShed_TempSetOnTrig = 120
					this.formData.lsShed_OffSensor = 20
					this.formData.lsShed_TempSetOffTrig = 130

					// Load Up row
					this.formData.lsLoadUp_TempSetHPWHOut = 140
					this.formData.lsLoadUp_AquaFrac = 20
					this.formData.lsLoadUp_TempSetOnTrig = 135
					this.formData.lsLoadUp_OffSensor = 20
					this.formData.lsLoadUp_TempSetOffTrig = 140
				}
				else if (['sprtp', 'sprtp_series', 'gas_parallel'].includes(newSchematic)) {
					// Normal row
					this.formData.storageT = 140
					this.formData.aquastatFraction = 40
					this.formData.lsNorm_TempSetOnTrig = 125
					this.formData.offSensor_normal = 20
					this.formData.lsNorm_TempSetOffTrig = 130

					// Shed row
					this.formData.lsShed_AquaFrac = 80
					this.formData.lsShed_TempSetOnTrig = 125
					this.formData.lsShed_OffSensor = 40
					this.formData.lsShed_TempSetOffTrig = 130

					// Load Up row
					this.formData.lsLoadUp_TempSetHPWHOut = 140
					this.formData.lsLoadUp_AquaFrac = 20
					this.formData.lsLoadUp_TempSetOnTrig = 125
					this.formData.lsLoadUp_OffSensor = 100 - this.formData.percentUseable
					this.formData.lsLoadUp_TempSetOffTrig = 130
				}
				else {
					// Normal row
					this.formData.storageT = 150
					this.formData.aquastatFraction = 40
					this.formData.lsNorm_TempSetOnTrig = 120
					this.formData.offSensor_normal = 20
					this.formData.lsNorm_TempSetOffTrig = 140

					// Shed row
					this.formData.lsShed_AquaFrac = 80
					this.formData.lsShed_TempSetOnTrig = 120
					this.formData.lsShed_OffSensor = 40
					this.formData.lsShed_TempSetOffTrig = 140

					// Load Up row
					this.formData.lsLoadUp_TempSetHPWHOut = 150
					this.formData.lsLoadUp_AquaFrac = 20
					this.formData.lsLoadUp_TempSetOnTrig = 120
					this.formData.lsLoadUp_OffSensor = 100 - this.formData.percentUseable
					this.formData.lsLoadUp_TempSetOffTrig = 125
				}
			},
			immediate: true
		}
	}
}
</script>

<style scoped>
.disabled-row {
	opacity: 0.5;
	pointer-events: none;
}
</style>
